import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatCityLabel } from "../utils/resolveRegion";

export default function FriendsPage() {
  const navigate = useNavigate();
  const currentUserId = Number(localStorage.getItem("user_id"));
  const [tab, setTab] = useState("friends"); // friends | blocked
  const [friends, setFriends] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadFriends = async () => {
    const res = await fetch(`/api/friends?current_user_id=${currentUserId}`);
    const data = await res.json();
    setFriends(Array.isArray(data) ? data : []);
  };

  const loadBlocked = async () => {
    const res = await fetch(`/api/friends/blocked?current_user_id=${currentUserId}`);
    const data = await res.json();
    setBlocked(Array.isArray(data) ? data : []);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([loadFriends(), loadBlocked()]);
    } catch (err) {
      console.error("加载好友数据失败:", err);
      setFriends([]);
      setBlocked([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUserId) {
      navigate("/login");
      return;
    }
    loadAll();
  }, [currentUserId, navigate]);

  const handleDeleteFriend = async (friendId, friendName) => {
    const ok = window.confirm(`确定要删除好友 ${friendName} 吗？`);
    if (!ok) return;

    try {
      const res = await fetch("/api/friends/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          current_user_id: currentUserId,
          friend_user_id: friendId,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "删除失败");
        return;
      }
      alert("删除好友成功");
      loadAll();
    } catch (err) {
      console.error("删除好友失败:", err);
      alert("删除失败");
    }
  };

  const handleBlock = async (userId, userName) => {
    const ok = window.confirm(
      `确定拉黑 ${userName} 吗？拉黑后将解除好友关系，双方无法互加好友与聊天。`
    );
    if (!ok) return;

    try {
      const res = await fetch("/api/friends/block", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          current_user_id: currentUserId,
          target_user_id: userId,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "拉黑失败");
        return;
      }
      alert(data.message || "已拉黑");
      setTab("blocked");
      loadAll();
    } catch (err) {
      console.error("拉黑失败:", err);
      alert("拉黑失败");
    }
  };

  const handleUnblock = async (userId, userName) => {
    const ok = window.confirm(`确定解除对 ${userName} 的拉黑吗？`);
    if (!ok) return;

    try {
      const res = await fetch("/api/friends/unblock", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          current_user_id: currentUserId,
          target_user_id: userId,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "解除失败");
        return;
      }
      alert(data.message || "已解除拉黑");
      loadAll();
    } catch (err) {
      console.error("解除拉黑失败:", err);
      alert("解除失败");
    }
  };

  const renderUserCard = (item, mode) => (
    <div key={item.id} className="border rounded-xl p-4 flex items-center gap-4">
      <img
        src={item.avatar ? `${item.avatar}` : "https://via.placeholder.com/56"}
        alt={item.name}
        className="w-14 h-14 rounded-full object-cover"
      />

      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate">{item.name}</p>
        <p className="text-sm text-gray-500">
          {item.age || "?"}岁 ·{" "}
          {formatCityLabel(item.province, item.city, item.district, "未填写城市")}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 justify-end">
        <button
          onClick={() => navigate(`/user/${item.id}`)}
          className="px-4 py-2 border rounded-lg text-sm"
        >
          查看资料
        </button>

        {mode === "friends" ? (
          <>
            <button
              onClick={() =>
                navigate("/chat", {
                  state: { user: { id: item.id, name: item.name } },
                })
              }
              className="px-4 py-2 bg-pink-500 text-white rounded-lg text-sm"
            >
              聊天
            </button>
            <button
              onClick={() => handleDeleteFriend(item.id, item.name)}
              className="px-4 py-2 text-red-500 border border-red-200 rounded-lg text-sm hover:bg-red-50"
            >
              删除
            </button>
            <button
              onClick={() => handleBlock(item.id, item.name)}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              拉黑
            </button>
          </>
        ) : (
          <button
            onClick={() => handleUnblock(item.id, item.name)}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-700"
          >
            解除拉黑
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">我的好友</h2>
        <button onClick={() => navigate("/center")} className="text-sm text-pink-500">
          返回个人中心
        </button>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setTab("friends")}
          className={`px-4 py-2 rounded-full text-sm border ${
            tab === "friends"
              ? "bg-pink-500 text-white border-pink-500"
              : "bg-white text-gray-600 border-gray-200"
          }`}
        >
          好友列表 {friends.length ? `(${friends.length})` : ""}
        </button>
        <button
          type="button"
          onClick={() => setTab("blocked")}
          className={`px-4 py-2 rounded-full text-sm border ${
            tab === "blocked"
              ? "bg-gray-800 text-white border-gray-800"
              : "bg-white text-gray-600 border-gray-200"
          }`}
        >
          黑名单 {blocked.length ? `(${blocked.length})` : ""}
        </button>
      </div>

      {loading ? (
        <p className="text-center text-gray-400 py-10">加载中...</p>
      ) : tab === "friends" ? (
        friends.length === 0 ? (
          <p className="text-center text-gray-400 py-10">你还没有好友</p>
        ) : (
          <div className="space-y-4">
            {friends.map((item) => renderUserCard(item, "friends"))}
          </div>
        )
      ) : blocked.length === 0 ? (
        <p className="text-center text-gray-400 py-10">黑名单为空</p>
      ) : (
        <div className="space-y-4">
          {blocked.map((item) => renderUserCard(item, "blocked"))}
        </div>
      )}
    </div>
  );
}
